"use client"

import { useState, useEffect } from "react"
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  Button,
  Tabs,
  Tab,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Pagination,
  TablePagination,
} from "@mui/material"
import {
  Search,
  Download,
  Visibility,
  AttachMoney,
  People,
  Store,
  Close,
  CheckCircle,
  Receipt,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
// import {
//   getAllCampaigns,
//   getCampaignFullDetails,
//   getCampaignVendors,
//   getVendorsCampaignPayment,
// } from "../../api/campaigns/SabiShare"
import { IonContent, IonPage } from "@ionic/react"
// import { convertToCSV, downloadCSV } from "../../common/utils_t"
import React from "react"
import { getVendorsCampaignPayment, getAllCampaigns, getCampaignFullDetails, getCampaignVendors } from "../../../api/campaigns/SabiShare"

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[2],
  "&:hover": {
    boxShadow: theme.shadows[4],
    transform: "translateY(-2px)",
    transition: "all 0.3s ease-in-out",
  },
}))

const DashboardContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}))

const ModalContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 800,
  maxHeight: "80vh",
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[24],
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}))

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  borderRadius: theme.spacing(1.5),
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(135deg, #1e1e2e 0%, #2d2d44 100%)"
      : "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
  border: `1px solid ${theme.palette.divider}`,
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[8],
  },
}))

const VendorPaymentHistory = ({ vendor, campaignId, open, onClose }) => {
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  useEffect(() => {
    if (open && vendor) {
      fetchVendorPayments(campaignId, vendor.vendor_id)
    }
  }, [open, vendor, campaignId])

  const fetchVendorPayments = async (campaignId, vendorId) => {
    setLoading(true)
    setPage(0)
    try {
      console.log("Fetching payments for vendor:", vendorId, "and campaign:", campaignId)
      const response = await getVendorsCampaignPayment(campaignId, vendorId)
      console.log("Vendor payments response:", response)
      setPayments(response.data)
    } catch (err) {
      setError("Failed to load payment history")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const paginatedLedger = payments.ledger
    ? payments.ledger.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">{payments.campaign?.name || "Campaign"} - Vendor Payment History</Typography>
            <Typography variant="body2" color="text.secondary">
              Campaign ID: {payments.campaign?.campaign_id}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {/* Campaign Info Summary */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: "background.default" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Campaign Budget
                </Typography>
                <Typography variant="h6" color="primary">
                  ${Number.parseFloat(payments.campaign?.budget || 0).toLocaleString()}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Campaign Status
                </Typography>
                <Chip
                  label={payments.campaign?.status || "Unknown"}
                  size="small"
                  color={payments.campaign?.status === "active" ? "success" : "default"}
                  variant="outlined"
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body2">{formatDate(payments.campaign?.start_date)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body2">{formatDate(payments.campaign?.end_date)}</Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Donor Information */}
        {payments.vendor && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                DONOR INFORMATION
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Full Name
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {payments.vendor.full_name}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Donor ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {payments.vendor.vendor_id}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Contact Information
                      </Typography>
                      <Typography variant="body2">{payments.vendor.email || "No email"}</Typography>
                      <Typography variant="body2">{payments.vendor.phone || "No phone"}</Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : payments.ledger && payments.ledger.length > 0 ? (
          <>
            {/* Payment Summary */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Payment Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <StatCard variant="outlined">
                    <Typography variant="caption" color="text.secondary" display="block">
                      Total Deposits
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      $
                      {payments.ledger
                        .reduce((sum, payment) => sum + Number.parseFloat(payment.drValue || 0), 0)
                        .toLocaleString()}
                    </Typography>
                  </StatCard>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatCard variant="outlined">
                    <Typography variant="caption" color="text.secondary" display="block">
                      Total Withdrawals
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      $
                      {payments.ledger
                        .reduce((sum, payment) => sum + Number.parseFloat(payment.crValue || 0), 0)
                        .toLocaleString()}
                    </Typography>
                  </StatCard>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatCard variant="outlined">
                    <Typography variant="caption" color="text.secondary" display="block">
                      Total Transactions
                    </Typography>
                    <Typography variant="h6">{payments.ledger.length}</Typography>
                  </StatCard>
                </Grid>
              </Grid>
            </Box>

            {/* Payment History Table with Pagination */}
            <Typography variant="subtitle1" gutterBottom>
              Transaction History ({payments.ledger?.length || 0} total)
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Transaction Type</TableCell>
                    <TableCell>Deposit</TableCell>
                    <TableCell>Withdrawal</TableCell>
                    <TableCell>Wallet ID</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLedger.map((payment, index) => {
                    const isDeposit = Number.parseFloat(payment.drValue || 0) > 0
                    const amount = isDeposit ? payment.drValue : payment.crValue

                    return (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2">{formatDate(payment.dateCreated)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(payment.dateCreated).toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={isDeposit ? "Deposit" : "Withdrawal"}
                            color={isDeposit ? "success" : "error"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {isDeposit ? (
                            <Typography variant="body2" fontWeight="medium" color="success.main">
                              ${Number.parseFloat(amount).toLocaleString()}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {!isDeposit ? (
                            <Typography variant="body2" fontWeight="medium" color="error.main">
                              ${Number.parseFloat(amount).toLocaleString()}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={payment.walletID}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.7rem",
                                maxWidth: "120px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {payment.walletID}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={payment.shopID === "wallet" ? "Wallet" : payment.shopID}
                            color="default"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label="Completed"
                            color="success"
                            icon={<CheckCircle fontSize="small" />}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, mb: 2, px: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {payments.ledger.length === 0 ? 0 : page * rowsPerPage + 1} to{" "}
                {Math.min((page + 1) * rowsPerPage, payments.ledger?.length || 0)} of {payments.ledger?.length || 0}{" "}
                transactions
              </Typography>
            </Box>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={payments.ledger?.length || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 1,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            />
          </>
        ) : (
          <Box textAlign="center" p={4}>
            <Receipt sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
            <Typography color="text.secondary" gutterBottom>
              No payment history found for this campaign.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are no recorded transactions for this campaign yet.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {payments.ledger && payments.ledger.length > 0 && (
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => {
              const csvContent = convertToCSV(payments.ledger)
              downloadCSV(csvContent, `campaign-payments-${payments.campaign?.campaign_id}.csv`)
            }}
          >
            Export CSV
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

// Main Component
const PaymentHistory = () => {
  const [campaigns, setCampaigns] = useState([])
  const [selectedCampaign, setSelectedCampaign] = useState("")
  const [campaignData, setCampaignData] = useState(null)
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState({
    campaigns: false,
    details: false,
    vendors: false,
  })
  const [error, setError] = useState(null)
  const [donorFilter, setDonorFilter] = useState("")
  const [vendorFilter, setVendorFilter] = useState("")
  const [activeTab, setActiveTab] = useState(0)
  const [selectedVendor, setSelectedVendor] = useState(null)
  const [showVendorModal, setShowVendorModal] = useState(false)
  const [vendorPage, setVendorPage] = useState(1)
  const vendorsPerPage = 9

  useEffect(() => {
    fetchCampaigns()
  }, [])

  useEffect(() => {
    if (selectedCampaign) {
      fetchCampaignData(selectedCampaign)
    }
  }, [selectedCampaign])

  const fetchCampaigns = async () => {
    setLoading((prev) => ({ ...prev, campaigns: true }))
    try {
      const response = await getAllCampaigns()
      setCampaigns(response.data)
      setError(null)
    } catch (err) {
      setError("Failed to load campaigns")
      console.error(err)
    } finally {
      setLoading((prev) => ({ ...prev, campaigns: false }))
    }
  }

  const fetchCampaignData = async (campaignId) => {
    setLoading({ details: true, vendors: true })
    try {
      const detailsResponse = await getCampaignFullDetails(campaignId)
      setCampaignData(detailsResponse.data)

      const vendorsResponse = await getCampaignVendors(campaignId)
      setVendors(vendorsResponse.data)

      setError(null)
    } catch (err) {
      setError("Failed to load campaign data")
      console.error(err)
    } finally {
      setLoading((prev) => ({ ...prev, details: false, vendors: false }))
    }
  }

  const handleCampaignChange = (event) => {
    setSelectedCampaign(event.target.value)
    setActiveTab(0)
    setCampaignData(null)
    setVendors([])
    setVendorPage(1)
  }

  const handleVendorClick = (vendor) => {
    setSelectedVendor(vendor)
    setShowVendorModal(true)
  }

  const formatCurrency = (amount) => {
    if (!amount) return "N0.00"
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "NGN",
    }).format(Number.parseFloat(amount))
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const getStats = () => {
    if (!campaignData) {
      return {
        totalDonations: 0,
        donorCount: 0,
        totalVouchers: 0,
        totalBudget: campaignData?.campaign?.budget || 0,
      }
    }

    const ledger = campaignData.ledger || []
    const totalDonations = ledger.reduce((sum, entry) => {
      return sum + Number.parseFloat(entry.drValue || 0)
    }, 0)

    const donorCount = campaignData.campaign?.donor ? 1 : 0
    const totalVouchers = vendors.reduce((sum, vendor) => {
      return sum + (vendor.total_vouchers || 0)
    }, 0)

    return {
      totalDonations,
      donorCount,
      totalVouchers,
      totalBudget: campaignData.campaign?.budget || 0,
    }
  }

  const stats = getStats()

  const filteredVendors = vendors.filter((vendor) => {
    const searchTerm = vendorFilter.toLowerCase()
    return (
      (vendor.firstName?.toLowerCase() || "").includes(searchTerm) ||
      (vendor.lastName?.toLowerCase() || "").includes(searchTerm) ||
      (vendor.business_name?.toLowerCase() || "").includes(searchTerm) ||
      (vendor.email?.toLowerCase() || "").includes(searchTerm)
    )
  })

  const totalPages = Math.ceil(filteredVendors.length / vendorsPerPage)
  const paginatedVendors = filteredVendors.slice((vendorPage - 1) * vendorsPerPage, vendorPage * vendorsPerPage)

  const handleVendorFilterChange = (e) => {
    setVendorFilter(e.target.value)
    setVendorPage(1)
  }

  return (
    <IonPage>
      <IonContent fullscreen>
        <DashboardContainer>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Withdrawal History Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track donor contributions and vendor payments across campaigns
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Campaign Selection */}
          <StyledCard sx={{ mb: 4, p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="campaign-select-label">Select Campaign</InputLabel>
                  <Select
                    labelId="campaign-select-label"
                    value={selectedCampaign}
                    label="Select Campaign"
                    onChange={handleCampaignChange}
                    disabled={loading.campaigns}
                    startAdornment={loading.campaigns && <CircularProgress size={20} sx={{ mr: 1 }} />}
                  >
                    <MenuItem value="">
                      <em>Select a campaign</em>
                    </MenuItem>
                    {campaigns.map((campaign) => (
                      <MenuItem key={campaign.campaign_id} value={campaign.campaign_id}>
                        {campaign.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Campaign Stats Cards */}
              {selectedCampaign && campaignData && (
                <>
                  <Grid item xs={12} sm={6} md={2}>
                    <StatCard variant="outlined">
                      <AttachMoney sx={{ color: "success.main", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Total Donations
                      </Typography>
                      <Typography variant="h6">{formatCurrency(stats.totalDonations)}</Typography>
                    </StatCard>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <StatCard variant="outlined">
                      <People sx={{ color: "primary.main", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Total Vendors
                      </Typography>
                      <Typography variant="h6">{vendors.length}</Typography>
                    </StatCard>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <StatCard variant="outlined">
                      <Store sx={{ color: "warning.main", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Vouchers
                      </Typography>
                      <Typography variant="h6">{stats.totalVouchers}</Typography>
                    </StatCard>
                  </Grid>
                </>
              )}
            </Grid>
          </StyledCard>

          {selectedCampaign && campaignData ? (
            <Box>
              {/* Tabs */}
              <Box sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                  <Tab label={`Donor Information`} />
                  <Tab label={`Vendors (${vendors.length})`} />
                </Tabs>
              </Box>

              {/* Donors Tab */}
              {activeTab === 0 && campaignData.campaign && (
                <Box>
                  <StyledCard sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Campaign Name
                            </Typography>
                            <Typography variant="h6">{campaignData.campaign.name}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Campaign ID
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                              {campaignData.campaign.campaign_id}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Campaign Status
                            </Typography>
                            <Chip
                              label={campaignData.campaign.status || "Unknown"}
                              color={campaignData.campaign.status === "active" ? "success" : "default"}
                              variant="outlined"
                            />
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Budget
                            </Typography>
                            <Typography variant="body1" fontWeight="medium" color="primary">
                              {formatCurrency(campaignData.campaign.budget)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </StyledCard>
                </Box>
              )}

              {activeTab === 1 && (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      placeholder="Search vendors by name, business, or email..."
                      value={vendorFilter}
                      onChange={handleVendorFilterChange}
                      InputProps={{
                        startAdornment: <Search sx={{ mr: 1, color: "action.active" }} />,
                      }}
                      size="small"
                    />
                  </Box>

                  {loading.vendors ? (
                    <Box display="flex" justifyContent="center" p={4}>
                      <CircularProgress />
                    </Box>
                  ) : paginatedVendors.length > 0 ? (
                    <>
                      {/* Vendors Grid */}
                      <Grid container spacing={3} sx={{ mb: 3 }}>
                        {paginatedVendors.map((vendor) => (
                          <Grid item xs={12} sm={6} md={4} key={vendor.vendor_id}>
                            <StyledCard variant="outlined" sx={{ height: "100%" }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                  <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
                                    {vendor.firstName?.charAt(0) || "V"}
                                  </Avatar>
                                  <Box flex={1}>
                                    <Typography variant="h6" sx={{ fontSize: "1rem" }}>
                                      {vendor.firstName} {vendor.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                      {vendor.business_name || "No business name"}
                                    </Typography>
                                  </Box>
                                </Stack>

                                <Divider sx={{ my: 2 }} />

                                <Stack spacing={1.5}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Email
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                      {vendor.email}
                                    </Typography>
                                  </Box>

                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Phone
                                    </Typography>
                                    <Typography variant="body2">{vendor.user_phone || "N/A"}</Typography>
                                  </Box>

                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Total Vouchers
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium" color="primary">
                                      {vendor.total_vouchers || 0}
                                    </Typography>
                                  </Box>

                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Vendor ID
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary" }}
                                    >
                                      {vendor.vendor_id}
                                    </Typography>
                                  </Box>
                                </Stack>

                                <Button
                                  fullWidth
                                  variant="contained"
                                  size="small"
                                  startIcon={<Visibility />}
                                  onClick={() => handleVendorClick(vendor)}
                                  sx={{ mt: 2 }}
                                >
                                  View Payment History
                                </Button>
                              </CardContent>
                            </StyledCard>
                          </Grid>
                        ))}
                      </Grid>

                      {totalPages > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
                          <Pagination
                            count={totalPages}
                            page={vendorPage}
                            onChange={(e, value) => setVendorPage(value)}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      )}

                      {/* Results Info */}
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 2 }}>
                        Showing {(vendorPage - 1) * vendorsPerPage + 1} to{" "}
                        {Math.min(vendorPage * vendorsPerPage, filteredVendors.length)} of {filteredVendors.length}{" "}
                        vendors
                      </Typography>
                    </>
                  ) : (
                    <Box textAlign="center" py={4}>
                      <People sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                      <Typography color="text.secondary" gutterBottom>
                        No vendors found for this campaign.
                      </Typography>
                      {vendorFilter && (
                        <Typography variant="body2" color="text.secondary">
                          Try adjusting your search filters.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box textAlign="center" py={6}>
              <Box sx={{ fontSize: 48, mb: 2, color: "text.disabled" }}>
                <Store />
              </Box>
              <Typography color="text.secondary" gutterBottom>
                Select a campaign to view payment history and vendors
              </Typography>
            </Box>
          )}
        </DashboardContainer>

        {/* Vendor Payment History Modal */}
        <VendorPaymentHistory
          vendor={selectedVendor}
          campaignId={selectedCampaign}
          open={showVendorModal}
          onClose={() => setShowVendorModal(false)}
        />
      </IonContent>
    </IonPage>
  )
}

export default PaymentHistory
